<template>
    <v-row justify="center">
        <v-dialog v-model="isOpen" @click:outside="closeWhenNoChanges()" @keydown.esc="closeWhenNoChanges()" persistent max-width="600px">
            <v-card>
                <v-form>
                    <v-card-title>
                        <span class="headline">
                            {{
                                formData.id
                                    ? $t('createOrUpdateHoliday.editTitle')
                                    : $t('createOrUpdateHoliday.createTitle')
                            }}</span
                        >
                    </v-card-title>
                    <v-card-text>
                        <v-container>
                            <v-row>
                                <v-col cols="12">
                                    <v-text-field
                                        v-model="formData.name"
                                        :label="
                                            $t(
                                                'createOrUpdateHoliday.forms.name'
                                            )
                                        "
                                        type="string"
                                        :error-messages="nameErrors"
                                        @input="$v.formData.name.$touch"
                                        @blur="$v.formData.name.$touch"
                                        @keyup="clearServerErrors('name')"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-menu
                                        v-model="isDatePickerVisible"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="290px"
                                    >
                                        <template
                                            v-slot:activator="{ on, attrs }"
                                        >
                                            <v-text-field
                                                :label="
                                                    $t(
                                                        'createOrUpdateHoliday.forms.date'
                                                    )
                                                "
                                                prepend-icon="mdi-calendar"
                                                readonly
                                                :value="
                                                    formData.date
                                                        | dateFormatFromSettings
                                                "
                                                :error-messages="dateErrors"
                                                v-bind="attrs"
                                                v-on="on"
                                            />
                                        </template>
                                        <v-date-picker
                                            v-model="formData.date"
                                            no-title
                                            scrollable
                                            :picker-date.sync="
                                                pickerYearAndMonth
                                            "
                                            :allowed-dates="allowedDates"
                                            @input="onDatePickerInput"
                                        />
                                    </v-menu>
                                </v-col>
                                <v-col cols="12">
                                    <v-checkbox
                                        v-model="formData.repeat"
                                        :label="
                                            $t(
                                                'createOrUpdateHoliday.forms.repeat'
                                            )
                                        "
                                    />
                                </v-col>
                            </v-row>
                        </v-container>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer />
                        <v-btn color="red darken-1" text @click="onClose">{{
                            $t('createOrUpdateHoliday.forms.close')
                        }}</v-btn>
                        <v-btn color="blue darken-1" text @click="onSave">{{
                            $t('createOrUpdateHoliday.forms.save')
                        }}</v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import dayjs from 'dayjs';
import { mapActions } from 'vuex';
import createOrEditValidator from '@/validators/holidays/createOrEditValidator.mixin';
import calculateHolidayDates from '@/services/calculateHolidayDates';

export default {
    name: 'createOrUpdateHoliday',
    props: {
        isOpen: {
            type: Boolean,
            required: false,
            default: false
        },
        holiday: {
            type: Object,
            required: false,
            default: () => ({})
        }
    },
    mixins: [createOrEditValidator],
    data() {
        const defaultFormData = {
            name: '',
            date: dayjs().format('YYYY-MM-DD'),
            repeat: false
        };

        return {
            formData: { ...defaultFormData },
            formDataCopy: { ...defaultFormData },
            defaultFormData,
            isDatePickerVisible: false,
            year: dayjs().year(),
            pickerYearAndMonth: dayjs().format('YYYY-MM'),
            holidays: null,
            holidayDates: null
        };
    },
    watch: {
        holiday() {
            this.setDefaultFormData();
        },

        pickerYearAndMonth: {
            immediate: true,
            async handler(newYearMonth, oldYearMonth) {
                const {
                    holidays,
                    holidayDates
                } = await calculateHolidayDates.handle(
                    newYearMonth,
                    oldYearMonth,
                    this.holidays
                );

                this.holidays = holidays;
                this.holidayDates = holidayDates;
            }
        },

        async isOpen(val) {
            if (!val) {
                return;
            }

            const {
                holidays,
                holidayDates
            } = await calculateHolidayDates.handle(this.pickerYearAndMonth);

            this.holidays = holidays;
            this.holidayDates = holidayDates;
        }
    },
    created() {
        this.setDefaultFormData();
    },
    methods: {
        ...mapActions({
            saveHoliday: 'holidays/save',
            fetchHolidaysForYear: 'holidays/fetchForYear'
        }),
        setDefaultFormData() {
            this.formData =
                this.holiday && this.holiday.id
                    ? { ...this.holiday }
                    : { ...this.defaultFormData };

            this.formDataCopy = { ...this.formData };
        },
        async onSave() {
            try {
                this.$v.$touch();

                if (!this.isValid) {
                    return;
                }

                await this.saveHoliday(this.formData);

                this.$notify({
                    group: 'notify',
                    type: 'success',
                    title: this.formData.id
                        ? this.$t('createOrUpdateHoliday.editTitle')
                        : this.$t('createOrUpdateHoliday.createTitle'),
                    text: this.$t('createOrUpdateHoliday.saveSuccess')
                });

                this.onClose();
            } catch (error) {
                if (this.checkForServerFormErrors(error)) {
                    this.$notify({
                        group: 'notify',
                        type: 'error',
                        title: this.formData.id
                            ? this.$t('createOrUpdateHoliday.editTitle')
                            : this.$t('createOrUpdateHoliday.createTitle'),
                        text: this.$t('createOrUpdateHoliday.checkFormErrors')
                    });
                }
            }
        },
        async onClose() {
            setTimeout(this.setDefaultFormData, 100);
            this.$v.$reset();
            this.$emit('close');
        },
        onDatePickerInput() {
            this.$v.formData.date.$touch();
            this.clearServerErrors('date');
            this.isDatePickerVisible = false;
        },

        allowedDates(pickerDate) {
            const dayOfMonth = dayjs(pickerDate).date();

            return !this.holidayDates.includes(dayOfMonth);
        },
        closeWhenNoChanges() {
            const currentData = Object.entries(this.formData).toString();
            const previousData = Object.entries(this.formDataCopy).toString();

            if (previousData !== currentData) {
                return;
            }

            this.onClose();
        }
    }
};
</script>
